/**
 * AIReviewCompletionScreen - Review completion screen for AI Review (MA-FE-073)
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  Wrench,
  Eye,
  Home,
  Sparkles,
  Edit3,
  ArrowRight,
} from "lucide-react";

export default function AIReviewCompletionScreen({
  stepsReviewed,
  stepsApproved,
  stepsEdited,
  onOpenBuilder,
  onPreview,
  onReturnToDashboard,
}) {
  return (
    <div className="flex-1 flex items-center justify-center bg-slate-950">
      <Card className="glass border-slate-700/50 max-w-md w-full mx-4">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
          </div>
          <CardTitle className="text-2xl text-slate-100">
            AI Review Complete
          </CardTitle>
          <p className="text-slate-400 mt-2">
            Your funnel is ready for final adjustments
          </p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 rounded-lg bg-slate-800/50">
              <p className="text-2xl font-bold text-slate-100">{stepsReviewed}</p>
              <p className="text-xs text-slate-400">Steps Reviewed</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-500/10">
              <p className="text-2xl font-bold text-emerald-400">{stepsApproved}</p>
              <p className="text-xs text-slate-400">Approved</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-500/10">
              <p className="text-2xl font-bold text-blue-400">{stepsEdited}</p>
              <p className="text-xs text-slate-400">Edited</p>
            </div>
          </div>
          
          {/* What's Next */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-slate-300">Next Steps</h4>
            <ul className="text-sm text-slate-400 space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">✓</span>
                Review complete - funnel structure looks good
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="h-4 w-4 mt-0.5 text-slate-500" />
                Open Builder to make final adjustments
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="h-4 w-4 mt-0.5 text-slate-500" />
                Fill in consent text before publishing
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="h-4 w-4 mt-0.5 text-slate-500" />
                Connect calendar for booking steps
              </li>
            </ul>
          </div>
          
          {/* Actions */}
          <div className="space-y-2 pt-4">
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={onOpenBuilder}
            >
              <Wrench className="h-4 w-4 mr-2" />
              Open Funnel Builder
            </Button>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1 border-slate-600 text-slate-300"
                onClick={onPreview}
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              
              <Button 
                variant="ghost" 
                className="flex-1 text-slate-400"
                onClick={onReturnToDashboard}
              >
                <Home className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
          
          {/* AI Badge */}
          <div className="text-center pt-2">
            <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
              <Sparkles className="h-3 w-3 mr-1" />
              Generated by FunnelAssist
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
